import format from 'date-fns/format';
import { graphql, PageProps } from 'gatsby';
import React, { useState } from 'react';
import { css } from '@emotion/react';
// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import { useLocation } from '@reach/router';
import Container from '../components/atoms/Container';
import PageTitle from '../components/atoms/PageTitle';
import SvgSafeGatsbyImage from '../components/atoms/SvgSafeGatsbyImage';
import Layout from '../components/Layout';
// import Breadcrumbs from '../components/sections/Breadcrumbs';
import apCaseOnlyTitleTags from '../components/utils/apCaseOnlyTitleTags';
import NearestVertical from '../components/franchise/nearest-vertical';
import Divider from '../components/primitives/divider';
import Col from '../components/primitives/grid/col';
import Row from '../components/primitives/grid/row';
import { breakpoints } from '../components/primitives/tokens';
import RichTextEditorStyles from '../components/utils/RichTextEditorStyles';
import Seo from '../components/utils/Seo';
import CtaProvider from '../contexts/franchise/cta-provider';
import FacebookIcon from '../images/svg/facebook.svg';
import LinkIcon from '../images/svg/article-link.svg';
import TwitterIcon from '../images/svg/article-twitter.svg';
import Link from '../components/primitives/link';
import { colors } from '../components/primitives/colors';
import { PageTemplate } from '../utils/match-page-template';

const styles = {
    container: css({
        marginTop: '24px',
    }),
    pageTitle: css({
        marginBottom: '12px',
        width: '66.666667%',
        [breakpoints.tb]: {
            width: '60%',
        },
        [breakpoints.mb]: {
            width: 'unset',
        },
    }),
    article: css({
        flex: '1 1 auto',
        [breakpoints.tb]: {
            flex: 'unset',
        },
    }),
    articleMeta: css({
        marginBottom: '40px',
        fontSize: '18px',
        lineHeight: '28px',
    }),
    shareContainer: css({
        display: 'flex',
        alignItems: 'center',
        margin: '16px 0',
    }),
    share: css({
        marginRight: '1rem',
        fontWeight: 600,
    }),
    shareLinkItem: css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }),
    shareLink: css({
        display: 'inline-flex',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        paddingRight: '12px',
        alignItems: 'center',
        color: colors.primaryOrange,
    }),
    copy: css({
        paddingBottom: '32px',
        fontSize: '20px',
        lineHeight: '28px',
    }),
    featuredImageContainer: css({
        marginBottom: '24px',
    }),
    featuredImage: css({
        width: '100%',
        marginBottom: '24px',
    }),
    divider: css({
        marginBottom: '24px',
    }),
    contentDivider: css({
        marginBottom: '24px',
        [breakpoints.tb]: {
            marginBottom: '32px',
        },
    }),
    sidebarContainer: css({
        position: 'sticky',
        top: '100px',
        paddingBottom: '24px',
        [breakpoints.tb]: {
            paddingBottom: '32px',
        },
    }),
    widget: css({
        padding: '32px',
        marginTop: '40px',
        marginBottom: '40px',
    }),
    widgetHeading: css({
        fontSize: '24px',
        lineHeight: '32px',
        fontWeight: 400,
    }),
    widgetCopy: css({
        paddingTop: '8px',
        paddingBottom: '8px',
    }),
    alert: css({
        display: 'block',
        backgroundColor: colors.shadesBlack,
        padding: '0.5rem',
        marginLeft: '0.5rem',
        borderRadius: '0.25rem',
        color: colors.shadesWhite,
        transitionProperty: 'opacity',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        transitionDuration: '300ms',
    }),
};

export type ArticlePageData = Queries.ArticleTemplateQuery;

export interface ArticlePageContext {
    hasLocationWidget: boolean,
    slug: string,
    title: string
    seo: ContentStackSeoData,
    templateName: PageTemplate.PressRelease | PageTemplate.BlogPost,
}

const Article: React.FC<PageProps<ArticlePageData, ArticlePageContext>> = ({ data, pageContext }) => {
    const {
        title,
        show_date,
        authors,
        content,
        featured_image,
        publish_details,
        sidebar_widgets,
        seo,
    } = data.cmsArticle || data.cmsPressRelease || {};
    const { hasLocationWidget } = pageContext;
    const { time: publish_time } = publish_details || {};
    const location = useLocation();
    const [copyAlertVisible, setCopyAlertVisible] = useState(false);

    return (
        <CtaProvider>
            <Layout>
                <Seo
                    title={seo?.meta_title || title}
                    description={seo?.meta_description}
                    imageUrl={seo?.meta_image?.url}
                />
                <Container css={styles.container}>
                    <PageTitle css={styles.pageTitle}>
                        {title || 'Untitled Article'}
                    </PageTitle>
                    <div css={styles.articleMeta}>
                        {!!show_date && !!publish_time && (
                            <p>
                                {format(new Date(publish_time), 'yyyy-MM-dd k:mm').toString()}
                            </p>
                        )}
                        {!!authors && (
                            <p>
                                By
                                {' '}
                                {authors}
                            </p>
                        )}
                    </div>
                    <Divider />
                    <div>
                        <div css={styles.shareContainer}>
                            <span css={styles.share}>Share: </span>
                            <li css={styles.shareLinkItem}>
                                <Link
                                    css={styles.shareLink}
                                    to={`http://www.facebook.com/sharer.php?u=${location.href}`}
                                    target="_blank"
                                >
                                    <FacebookIcon />
                                </Link>
                            </li>
                            <li css={styles.shareLinkItem}>
                                <Link
                                    css={styles.shareLink}
                                    to={`http://twitter.com/share?text=${location.href}`}
                                    target="_blank"
                                >
                                    <TwitterIcon />
                                </Link>
                            </li>
                            <li css={styles.shareLinkItem}>
                                <button
                                    type="button"
                                    css={styles.shareLink}
                                    onClick={() => {
                                        setCopyAlertVisible(true);
                                        navigator.clipboard.writeText(location.href);
                                        setTimeout(() => setCopyAlertVisible(false), 700);
                                    }}
                                >
                                    <LinkIcon />
                                </button>
                                <span
                                    css={styles.alert}
                                    style={{
                                        opacity: copyAlertVisible ? 1 : 0,
                                    }}
                                >
                                    Copied!
                                </span>
                            </li>
                        </div>
                    </div>
                    <Divider css={styles.divider} />
                    <Row>
                        {/* TODO: check if its supposed to accept children? Getting Type Error might be outside the scope of this PR */}
                        <Col breakpoints={{
                            dt: { span: hasLocationWidget ? 8 : 12 },
                            tb: { span: 12 },
                        }}
                        >
                            <article css={styles.article}>
                                {featured_image?.url && (
                                    <div css={styles.featuredImageContainer}>
                                        <SvgSafeGatsbyImage
                                            image={featured_image}
                                            alt={featured_image.description || ''}
                                            css={styles.featuredImage}
                                        />
                                        <Divider />
                                    </div>
                                )}
                                <div
                                    css={[RichTextEditorStyles, styles.copy]}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: content || '' }}
                                />
                                <Divider css={styles.contentDivider} />
                            </article>
                        </Col>
                        {hasLocationWidget && (
                            // TODO: same as the above
                            <Col breakpoints={{
                                dt: { span: 4 },
                                tb: { span: 12 },
                            }}
                            >
                                <div css={styles.sidebarContainer}>
                                    <NearestVertical />

                                    {(sidebar_widgets || []).map((sb) => {
                                        if (sb) {
                                            return (
                                                <div css={styles.widget} key={sb.heading}>
                                                    <h2 css={styles.widgetHeading}>
                                                        {apCaseOnlyTitleTags(sb.heading)}
                                                    </h2>
                                                    <p css={styles.widgetCopy}>{sb.copy}</p>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </Col>
                        )}
                    </Row>
                </Container>
            </Layout>
        </CtaProvider>
    );
};

export const query = graphql`
  query ArticleTemplate ($slug: String!) {
    cmsArticle(url: { eq: $slug }) {
      title
      url
      show_date
      authors
      content
      featured_image {
        filename
        title
        description
        url
        contentStackAsset(width: 1440)
      }
      publish_details {
        time
      }
      settings {
        is_featured
        service_line {
          title
          icon {
            filename
            title
            url
            contentStackAsset(width: 1440)
          }
        }
      }
      seo {
        meta_title
        meta_description
        meta_image {
          url
        }
      }
      sidebar_widgets {
        copy
        heading
      }
    }
    cmsPressRelease(url: { eq: $slug }) {
      title
      url
      show_date
      authors
      content
      featured_image {
        filename
        title
        description
        url
        contentStackAsset(width: 1440)
      }
      publish_details {
        time
      }
      seo {
        meta_title
        meta_description
        meta_image {
          url
        }
      }
      sidebar_widgets {
        copy
        heading
      }
    }
  }
`;

export default Article;
